// ============ CASE STUDY INDEX ============
// Just enough of each case study to render a card: the homepage strip, the
// /work grid, and the sitemap need these five fields and nothing else.
//
// This is the source of truth for them — caseStudies.js imports this module and
// merges the long-form content on top, so a title can never say one thing on a
// card and another on the page. The split is a load-time one: the full content
// is ~10 kB gzipped and only the /work/:slug route should pay for it.
//
// Plain ESM with no imports, same as caseStudies.js, because api/sitemap.js
// imports it too. See the note there about the file extension.

export const caseStudyIndex = [
  {
    slug: 'lobstack',
    project: 'Lobstack',
    image: '/images/showcase/lobstack.jpg',
    title: 'A receipt for every model call',
    kicker: 'Agent infrastructure',
    summary:
      'An OpenAI-compatible gateway whose real output is not the routing. It is the line underneath: which model served the call, what it cost, and what any saving was measured against.',
  },
  {
    slug: 'circuit',
    project: 'Circuit',
    image: '/images/case-studies/circuit.jpg',
    title: 'The conversation is the canvas',
    kicker: 'Agent tooling · Open source',
    summary:
      'An MCP server that turns a sentence into a workflow board and then runs it one deterministic step at a time — on the connectors you already gave Claude, holding no credentials of its own.',
  },
  {
    slug: 'glowup',
    project: 'GLOWUP',
    image: '/images/case-studies/glowup.jpg',
    title: 'The app holds no secrets',
    kicker: 'Mobile · AI safety',
    summary:
      'A consumer photo-analysis app where the phone never sees a model key, every result passes a schema and hard bounds before it is saved, and minors never get a generated image.',
  },
  {
    slug: 'roam',
    project: 'ROAM',
    image: '/images/case-studies/roam.jpg',
    title: 'The model never makes up a number',
    kicker: 'Mobile · Ranking',
    summary:
      'Travel ranking where deterministic engines compute every score and fact, and the language model may only explain them, with its citations checked against the evidence it was given.',
  },
  {
    slug: 'otto',
    project: 'Otto',
    image: '/images/case-studies/otto.jpg',
    title: 'Four checks before a stranger answers',
    kicker: 'Voice AI · Trust',
    summary:
      'A phone agent where the AI disclosure is built in code and verified three times, the number gate fails closed, and the thing it is least allowed to do is spend your money.',
  },
  {
    slug: 'site',
    project: 'This site',
    image: '/images/case-studies/site.jpg',
    title: 'A page that cannot lie to you',
    kicker: 'Web · Trust',
    summary:
      'The rebuild of brandononchain.com: unsupported claims deleted, status computed daily instead of typed, weight enforced in CI, and every demo labelled with what it is really showing.',
  },
  {
    slug: 'arcis',
    project: 'Arcis',
    image: '/images/showcase/arcis.jpg',
    title: 'A treasury an agent can use',
    kicker: 'DeFi · Base mainnet',
    summary:
      'Seven contracts giving autonomous agents somewhere to hold idle USDC, credit priced off an identity score, and a keeper that harvests, guards the reserve and watches its own gas.',
  },
  {
    slug: 'sentric',
    project: 'Sentric',
    image: '/images/showcase/sentric.jpg',
    title: 'One answer, a tenth of a cent, no account',
    kicker: 'Solana · Agent data',
    summary:
      'Solana wallet activity turned into a scored signal an agent can buy per call over x402, with the conviction weights published rather than hidden.',
  },
  {
    slug: 'outreach',
    project: 'Outreach',
    image: '/images/case-studies/outreach.jpg',
    title: 'Where the address came from decided everything',
    kicker: 'Pipelines · Deliverability',
    summary:
      'A campaign that learned the hard way that a plausible address is not a contact, and the guardrails that came out of it. Figures refresh from the send log.',
  },
  {
    slug: 'swarm-gcp',
    project: 'swarm-gcp',
    image: '/images/case-studies/swarm-gcp.jpg',
    title: 'The same pipeline, on managed pieces',
    kicker: 'Google Cloud · Infrastructure',
    summary:
      'The lead swarm ported to Cloud Run jobs, BigQuery and Pub/Sub in Terraform, with one identity per component and a walkthrough that teaches the stack. Built, not yet deployed.',
  },
  {
    slug: 'jobhunter',
    project: 'Jobhunter',
    image: '/images/case-studies/jobhunter.jpg',
    title: 'Retrieval was the easy half',
    kicker: 'Retrieval · Agent systems',
    summary:
      'A job-search agent that scored roles with hybrid retrieval, then learned the expensive way that a plausible email address is not a contact. One in four inferred addresses delivered; published ones, nine in ten.',
  },
];

export default caseStudyIndex;
